/**
 * Branded sign-in view for Enterprise Support Bank ("ESB").
 *
 * A polished two-panel online-banking sign-in that drives the
 * {@link ./AuthContext} actions. Slice C3 runs a TWO-step flow: the SPA
 * authenticates directly against Cognito, which has TOTP (authenticator-app)
 * MFA on. The first card collects email + password and calls
 * {@link AuthState.submitCredentials}; when Cognito raises a TOTP MFA challenge
 * the same card slot switches to a one-time-code step that calls
 * {@link AuthState.submitOtp} with the current authenticator app code (or
 * {@link AuthState.cancelOtp} to return to the password step).
 *
 * Styling and layout live in {@link ../ui/signin.css}; the brand mark is
 * {@link ../ui/BrandLogo}. This component stays presentational (it owns only
 * local field state) — all network/token logic lives in the auth context.
 *
 * Accessibility (R9.7): every input keeps the `<label htmlFor>`/`useId` pairing,
 * errors render in a `role="alert"` banner, focus states are visible, and all
 * controls are keyboard operable.
 */

import { useId, useState, type FormEvent } from 'react';

import { BrandLogo, LockGlyph, ShieldGlyph } from '../ui/BrandLogo.js';
import '../ui/signin.css';
import { useAuth } from './AuthContext.js';

const CURRENT_YEAR = new Date().getFullYear();

/** The security-reassurance line shown under the form. */
function SecurityLine(): JSX.Element {
  return (
    <p className="esb-security">
      <LockGlyph />
      <span>Secured with bank-grade encryption. Never share your password.</span>
    </p>
  );
}

/** The first sign-in step: collect email + password and authenticate. */
function SignInForm(): JSX.Element {
  const { submitCredentials, pending, error } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const emailId = useId();
  const passwordId = useId();

  function onSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void submitCredentials(email.trim(), password);
  }

  const canSubmit = email.trim().length > 0 && password.length > 0 && !pending;

  return (
    <form onSubmit={onSubmit} aria-labelledby="signin-heading" noValidate>
      <h1 id="signin-heading" className="esb-card__heading">
        Sign in to online banking
      </h1>
      <p className="esb-card__sub">Enter your details to continue securely.</p>

      {error !== undefined ? (
        <p className="esb-alert" role="alert" data-testid="signin-error">
          <span aria-hidden="true">⚠</span>
          <span>{error}</span>
        </p>
      ) : null}

      <div className="esb-field">
        <label className="esb-field__label" htmlFor={emailId}>
          Email
        </label>
        <input
          className="esb-field__input"
          id={emailId}
          name="email"
          type="email"
          autoComplete="username"
          placeholder="you@example.co.za"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          disabled={pending}
          required
        />
      </div>

      <div className="esb-field">
        <label className="esb-field__label" htmlFor={passwordId}>
          Password
        </label>
        <input
          className="esb-field__input"
          id={passwordId}
          name="password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          disabled={pending}
          required
        />
      </div>

      <button type="submit" className="esb-btn esb-btn--primary" disabled={!canSubmit}>
        {pending ? 'Signing in…' : 'Sign in'}
      </button>

      <SecurityLine />
    </form>
  );
}

/**
 * The second sign-in step: collect the current 6-digit code from the user's
 * authenticator app (TOTP). The code rotates every 30s inside the app, so there
 * is no "resend" — the user just reads the current code.
 */
function OtpForm(): JSX.Element {
  const { submitOtp, cancelOtp, pending, error, otpMessage } = useAuth();
  const [code, setCode] = useState('');
  const codeId = useId();

  function onSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void submitOtp(code);
  }

  const canSubmit = code.trim().length > 0 && !pending;

  return (
    <form onSubmit={onSubmit} aria-labelledby="otp-heading" noValidate>
      <h1 id="otp-heading" className="esb-card__heading">
        Enter the 6-digit code from your authenticator app
      </h1>
      {otpMessage !== undefined ? (
        <p className="esb-otp-message">{otpMessage}</p>
      ) : null}

      {error !== undefined ? (
        <p className="esb-alert" role="alert" data-testid="otp-error">
          <span aria-hidden="true">⚠</span>
          <span>{error}</span>
        </p>
      ) : null}

      <div className="esb-field">
        <label className="esb-field__label" htmlFor={codeId}>
          Authenticator code
        </label>
        <input
          className="esb-field__input esb-field__input--otp"
          id={codeId}
          name="one-time-code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="\d*"
          maxLength={6}
          value={code}
          onChange={(event) => setCode(event.target.value)}
          disabled={pending}
          required
        />
      </div>

      <button type="submit" className="esb-btn esb-btn--primary" disabled={!canSubmit}>
        {pending ? 'Verifying…' : 'Verify'}
      </button>

      <button
        type="button"
        className="esb-btn esb-btn--ghost"
        onClick={() => cancelOtp()}
        disabled={pending}
      >
        Back to sign in
      </button>

      <SecurityLine />
    </form>
  );
}

/**
 * The sign-in view. Renders the branded two-panel shell (hero + form card). The
 * card slot switches on the auth status: the email + password step by default,
 * and the authenticator-app code step when a TOTP MFA challenge is pending.
 * Shown by {@link ../App} whenever the user is not authenticated.
 */
export function SignIn(): JSX.Element {
  const { status } = useAuth();

  return (
    <div className="esb-signin">
      <header className="esb-appbar">
        <BrandLogo />
      </header>

      <div className="esb-signin__body">
        <section className="esb-hero" aria-label="About Enterprise Support Bank">
          <BrandLogo variant="onDark" className="esb-logo--hero" />
          <p className="esb-hero__tagline">Banking that backs you.</p>
          <p className="esb-hero__lead">
            Secure, everyday banking built around your business and your family &mdash; wherever
            you are in South Africa.
          </p>
          <ul className="esb-hero__bullets">
            <li className="esb-hero__bullet">
              <ShieldGlyph />
              <span>24/7 secure access</span>
            </li>
            <li className="esb-hero__bullet">
              <ShieldGlyph />
              <span>Instant payments &amp; transfers</span>
            </li>
            <li className="esb-hero__bullet">
              <ShieldGlyph />
              <span>Real-time spend insights</span>
            </li>
          </ul>
          <p className="esb-hero__flag">
            <span className="esb-hero__flag-dot" aria-hidden="true" />
            <span>Proudly South African</span>
          </p>
        </section>

        <main className="esb-formpanel">
          <div className="esb-card">
            {status === 'otpRequired' ? <OtpForm /> : <SignInForm />}
          </div>
        </main>
      </div>

      <footer className="esb-footer">
        <p>
          Enterprise Support Bank Ltd is an authorised financial services provider (FSP) and
          registered credit provider (NCR). Your deposits are protected.
        </p>
        <p>&copy; {CURRENT_YEAR} Enterprise Support Bank Ltd. All rights reserved.</p>
      </footer>
    </div>
  );
}
